// Flat chibi raiders: big round heads, tiny bodies, thick dark outlines and
// flat colours with one shade tone. Each character is drawn as an SVG string
// from a small "look" object (see CHARACTERS in avatarConfig.js), so the same
// function serves React pages and the game iframe.
//
// Poses are CSS animations on named groups (see AVATAR_CSS): idle, guard,
// walk, ready, taunt, grab, cheer, wave and sad. `flag` puts a flag of that colour
// in the left hand in place of a shield or bow.
//
// Drawing space: the character stands in a 200-wide box with its feet at
// (100, 228); the view box adds room for hats, weapons and the flag.

const O = "#2b1f2a"; // outline
const SW = 4.5;
const GOLD = "#f2c94c";
// The admin's "Majestic" outfit (Jack, 2026-10-23): all white with gold.
const MAJ_WHITE = "#f7f8fc";
const MAJ_SHADE = "#c9d3ea";
const MAJ_SILVER = "#aab6cc";
const MAJ_HAIR = "#eef0f4";
const STEEL = "#9aa3ad";
const WOOD = "#8a5a2b";

export const VIEWBOX = { x: -24, y: -16, w: 240, h: 256 };
// Where the feet are, as a fraction of the drawing's width and height.
export const FEET = { x: (100 - VIEWBOX.x) / VIEWBOX.w, y: (228 - VIEWBOX.y) / VIEWBOX.h };

const st = (fill, w = SW) => `fill="${fill}" stroke="${O}" stroke-width="${w}" stroke-linejoin="round" stroke-linecap="round"`;
const line = (d, colour, w) => `<path d="${d}" fill="none" stroke="${colour}" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round"/>`;
const shade = (d, op = 0.14) => `<path d="${d}" fill="#000" opacity="${op}"/>`;
const grp = (cls, inner) => (inner ? `<g class="${cls}">${inner}</g>` : "");

function darken(hex, f) {
  const n = parseInt(hex.slice(1), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => Math.round(v * (1 - f)));
  return `#${c.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

// ── Body ──
function legs(L) {
  const leg = (x) =>
    `<rect x="${x}" y="196" width="20" height="22" rx="6" ${st(L.legColour)}/>` +
    `<path d="M${x - 4} 214 h26 v10 a4 4 0 0 1 -4 4 h-22 a4 4 0 0 1 -4 -4 z" ${st(L.bootColour)}/>`;
  return grp("jav-leg-l", leg(78)) + grp("jav-leg-r", leg(102));
}

function belt() {
  return `<rect x="68" y="178" width="64" height="10" rx="3" ${st("#3d2a1e")}/><rect x="94" y="176" width="12" height="14" rx="2" ${st(GOLD)}/>`;
}

function torso(L) {
  const c = L.outfitColour;
  const t = L.trim;
  const female = L.body === "female";
  if (L.outfit === "majestic") {
    // Gandalf the White sits cross-legged: a white gown down to his lap,
    // a silver rope belt, then his crossed legs wrapped in the gown.
    return `<path d="M70 148 q0 -10 10 -10 h40 q10 0 10 10 q8 30 16 54 h-92 q8 -24 16 -54z" ${st(MAJ_WHITE)}/>` +
      `<path d="M104 140 h16 q10 0 10 10 q8 30 14 52 h-34 q6 -36 -6 -62z" fill="${MAJ_SHADE}" opacity=".55"/>` +
      `<path d="M72 176 q28 8 56 0" fill="none" stroke="${MAJ_SILVER}" stroke-width="5" stroke-linecap="round"/>` +
      line("M96 179 q-4 10 -2 18 M104 179 q4 10 2 18", MAJ_SILVER, 3) +
      `<path d="M52 204 q48 -18 96 0 q12 8 6 16 q-54 12 -108 0 q-6 -8 6 -16z" ${st(MAJ_WHITE)}/>` +
      `<path d="M100 196 q30 0 48 8 q12 8 6 16 q-28 6 -54 6z" fill="${MAJ_SHADE}" opacity=".55"/>` +
      line("M100 200 q-8 10 0 22 M70 208 q14 4 26 2 M130 208 q-14 4 -26 2", MAJ_SHADE, 3) +
      `<path d="M58 212 q-6 -2 -8 4 q2 6 10 6 h12 q2 -6 -2 -10z" ${st(MAJ_SILVER, 3.5)}/>` +
      `<path d="M142 212 q6 -2 8 4 q-2 6 -10 6 h-12 q-2 -6 2 -10z" ${st(MAJ_SILVER, 3.5)}/>`;
  }
  if (L.outfit === "robe") {
    return `<path d="M66 148 q0 -10 10 -10 h48 q10 0 10 10 l8 56 q0 6 -6 6 h-72 q-6 0 -6 -6 z" ${st(c)}/>` +
      shade("M104 140 h20 q10 0 10 10 l8 56 h-38 z") +
      `<path d="M100 141 v67" stroke="${t}" stroke-width="6"/>` +
      `<rect x="70" y="170" width="60" height="8" rx="3" ${st(t, 3.5)}/>`;
  }
  if (L.outfit === "dress") {
    return `<path d="M74 148 q0 -10 10 -10 h32 q10 0 10 10 l16 58 q0 6 -6 6 h-72 q-6 0 -6 -6 z" ${st(c)}/>` +
      shade("M104 140 h12 q10 0 10 10 l16 58 h-38 z") +
      `<path d="M66 204 h68" stroke="${t}" stroke-width="5"/>` +
      `<rect x="72" y="166" width="56" height="8" rx="3" ${st(t, 3.5)}/>` +
      `<path d="M88 141 l12 12 l12 -12" fill="none" stroke="${t}" stroke-width="4" stroke-linecap="round"/>`;
  }
  const shape = female
    ? "M72 150 q0 -10 10 -10 h36 q10 0 10 10 l6 44 q0 6 -6 6 h-56 q-6 0 -6 -6 z"
    : "M70 150 q0 -10 10 -10 h40 q10 0 10 10 v44 q0 6 -6 6 h-48 q-6 0 -6 -6 z";
  let s = `<path d="${shape}" ${st(c)}/>` + shade("M104 142 h14 q8 0 8 8 l4 44 q0 4 -4 4 h-22 z");
  if (L.outfit === "armour") s += `<path d="M100 142 v56" stroke="${t}" stroke-width="8"/>`;
  else if (L.outfit === "leather") s += line("M74 146 L126 192", O, 10) + line("M74 146 L126 192", t, 5);
  else s += line("M88 142 l12 13 l12 -13", t, 4);
  return s + belt();
}

function sleeveColour(L) {
  if (L.outfit === "majestic") return MAJ_WHITE;
  if (L.outfit === "armour") return "#8e979f";
  if (L.outfit === "leather") return L.skin;
  return L.outfitColour;
}

function arm(L, side) {
  const x = side < 0 ? 62 : 138;
  const hx = side < 0 ? 58 : 142;
  let s = `<rect x="${x - 8}" y="150" width="16" height="30" rx="8" ${st(sleeveColour(L))}/>`;
  if (L.outfit === "armour") s += `<circle cx="${x}" cy="148" r="11" ${st("#b8c0c8")}/>`;
  if (L.weapon === "gloves") return s + glove(hx, side);
  return s + `<circle cx="${hx}" cy="184" r="9" ${st(L.skin)}/>`;
}

// A red boxing glove with a white cuff, in place of the hand.
function glove(hx, side) {
  const red = "#d63031";
  // Inside a .jav-glow element (the head-to-head screen) the glove flashes
  // through the colours with sparkles twinkling on it (Jack, 2026-10-23).
  // Sparkles light up in a wave that sweeps across the glove, then repeats.
  const WAVE_S = 1.4;
  const star = (dx, dy, r, colour) => `<g transform="translate(${hx + side * dx} ${194 + dy})">` +
    `<path class="jav-sparkle" style="animation-delay:${(-WAVE_S * (1 - (dx + dy * 0.4 + 16) / 32)).toFixed(2)}s" ` +
    `d="M0 ${-r} Q0 0 ${r} 0 Q0 0 0 ${r} Q0 0 ${-r} 0 Q0 0 0 ${-r}Z" fill="${colour}"/></g>`;
  const sparkles = [[-13, 2, 3.5, "#b9ff6b"], [-9, -6, 5, "#fff"], [-10, 4, 4, "#ff7ad9"], [-6, 9, 4, "#6ff0ff"],
    [-3, -9, 4, "#ffe14d"], [-2, 0, 6, "#fff"], [2, 8, 5, "#ffe14d"], [4, -6, 5, "#6ff0ff"], [7, 2, 5, "#ff7ad9"],
    [9, 8, 4, "#fff"], [10, -4, 4, "#b9ff6b"]].map((a) => star(...a)).join("");
  // A soft light glow around the outside of the glove (Jack, 2026-10-23).
  const halo = `<radialGradient id="jav-glove-glow"><stop offset=".5" stop-color="#fff" stop-opacity=".55"/>` +
    `<stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient>` +
    `<circle class="jav-glove-halo" cx="${hx - side * 3}" cy="194" r="30" fill="url(#jav-glove-glow)"/>`;
  return `<g class="jav-glove">${halo}<rect x="${hx - 10}" y="172" width="20" height="12" rx="4" ${st("#f4ecd8", 3.5)}/>` +
    `<ellipse class="jav-glove-fill" cx="${hx}" cy="194" rx="16" ry="15" ${st(red)}/>` +
    `<ellipse class="jav-glove-fill" cx="${hx - side * 13}" cy="196" rx="6" ry="8" ${st(red, 3.5)}/>` +
    shade(`M${hx + side * 2} 181 a15 14 0 0 1 0 28 q${side * 10} -14 0 -28z`, 0.18) +
    `<ellipse cx="${hx - side * 5}" cy="187" rx="5" ry="3" fill="#fff" opacity=".45"/>${sparkles}</g>`;
}

// ── Head ──
function headBase(skin) {
  return `<ellipse cx="100" cy="96" rx="60" ry="54" ${st(skin)}/>` +
    `<path d="M100 42 a60 54 0 0 1 0 108 q30 -10 30 -54 q0 -44 -30 -54z" fill="#000" opacity=".08"/>`;
}

function ears(L) {
  if (L.ears !== "pointy") return "";
  return `<path d="M44 100 q-26 -2 -36 -22 q22 -2 38 8z" ${st(L.skin)}/>` +
    `<path d="M156 100 q26 -2 36 -22 q-22 -2 -38 8z" ${st(L.skin)}/>`;
}

// Eyes, brows, blush and mouth, drawn around eye height `cy`.
function face(L, cy, mood) {
  const female = L.body === "female";
  const browCol = L.hair === "bald" ? O : darken(L.hairColour, 0.45);
  let s = "";
  for (const [ex, side] of [[80, -1], [120, 1]]) {
    if (mood === "happy") {
      s += line(`M${ex - 7} ${cy + 2} q7 -10 14 0`, O, 4);
    } else {
      s += `<ellipse cx="${ex}" cy="${cy}" rx="6" ry="8" fill="${O}"/>`;
      if (L.eyes !== O) s += `<ellipse cx="${ex}" cy="${cy + 1.5}" rx="3.6" ry="5" fill="${L.eyes}"/>`;
      s += `<circle cx="${ex + 2}" cy="${cy - 3}" r="2.2" fill="#fff"/>`;
      if (female) s += line(`M${ex + side * 5} ${cy - 5} l${side * 6} -4 M${ex + side * 1} ${cy - 8} l${side * 3} -6`, O, 3);
    }
    const by = cy - 17;
    let brow;
    if (mood === "fierce") brow = `M${ex + side * 10} ${by - 3} L${ex - side * 8} ${by + 5}`;
    else if (mood === "sad") brow = `M${ex + side * 10} ${by + 4} L${ex - side * 8} ${by - 3}`;
    else brow = `M${ex - 9} ${by + 1} q9 -6 18 0`;
    s += line(brow, browCol, female ? 3.5 : 5);
  }
  s += `<ellipse cx="68" cy="${cy + 13}" rx="7" ry="4" fill="#ff7a8a" opacity=".42"/><ellipse cx="132" cy="${cy + 13}" rx="7" ry="4" fill="#ff7a8a" opacity=".42"/>`;
  const my = cy + 14;
  if (mood === "happy") s += `<path d="M89 ${my - 1} q11 14 22 0 z" fill="#7a2e2e" ${st("#7a2e2e", 3)}/>`;
  else if (mood === "sad") s += line(`M92 ${my + 5} q8 -7 16 0`, O, 3.5);
  else if (mood === "fierce") s += line(`M93 ${my + 3} q8 2 14 -5`, O, 3.5);
  else if (female) s += `<path d="M93 ${my} q7 8 14 0 q-7 3 -14 0z" fill="#d9566b" stroke="${O}" stroke-width="2.5" stroke-linejoin="round"/>`;
  else s += line(`M93 ${my} q7 7 14 0`, O, 3.5);
  return s;
}

// Beards, masks and other things on the face, for eye height `cy`.
function faceExtra(L, cy) {
  const h = L.hairColour;
  const dy = cy - 110;
  const at = (inner) => (dy ? `<g transform="translate(0 ${dy})">${inner}</g>` : inner);
  switch (L.face) {
    case "beard":
      return at(`<path d="M48 104 q4 58 52 66 q48 -8 52 -66 q-16 22 -52 22 q-36 0 -52 -22z" ${st(h)}/>` +
        `<path d="M82 128 q18 -10 36 0 q-18 8 -36 0z" ${st(h, 3)}/>`);
    case "bigbeard":
      return at(`<path d="M50 104 q2 74 50 82 q48 -8 50 -82 q-16 26 -50 26 q-34 0 -50 -26z" ${st(h)}/>` +
        `<path d="M82 128 q18 -10 36 0 q-18 10 -36 0z" ${st(h, 3)}/>` +
        line("M86 150 q4 12 0 22 M114 150 q-4 12 0 22", darken(h, 0.2), 3));
    case "wizardbeard":
      // Gandalf's long flowing beard, down to the chest.
      return at(`<path d="M48 104 q0 52 22 80 q14 18 30 32 q16 -14 30 -32 q22 -28 22 -80 q-16 26 -52 26 q-36 0 -52 -26z" ${st(h)}/>` +
        `<path d="M78 130 q22 -14 44 0 q-8 10 -22 4 q-14 6 -22 -4z" ${st(h, 3)}/>` +
        line("M82 150 q4 22 12 42 M118 150 q-4 22 -12 42 M100 140 v60", darken(h, 0.18), 3));
    case "stubble":
      // Stubble: a short beard a shade darker than the hair.
      return at(`<path d="M58 128 q8 28 42 30 q34 -2 42 -30 q-6 10 -18 12 q-8 -6 -24 -4 q-16 -2 -24 4 q-12 -2 -18 -12z" ${st(darken(h, 0.45), 3)}/>`);
    case "moustache":
      return at(`<path d="M80 128 q10 -10 20 -4 q10 -6 20 4 q-10 6 -20 0 q-10 6 -20 0z" ${st(h, 3)}/>`);
    case "mask":
      return at(`<path d="M46 120 q54 -10 108 0 v4 q-4 30 -54 34 q-50 -4 -54 -34z" ${st(L.trim)}/>` +
        shade("M100 116 q30 0 54 4 v4 q-4 30 -54 34z", 0.12));
    case "tusks":
      return at(`<path d="M86 ${132} l3 -13 l6 12z" ${st("#fff", 3)}/><path d="M114 132 l-3 -13 l-6 12z" ${st("#fff", 3)}/>`);
    case "warpaint":
      return at(line("M56 118 l16 4 M58 126 l14 2 M144 118 l-16 4 M142 126 l-14 2", L.trim, 4));
    case "eyepatch":
      return at(line("M42 84 L158 116", O, 4) + `<ellipse cx="120" cy="110" rx="11" ry="11" ${st(O, 3)}/>`);
    default:
      return "";
  }
}

// ── Hair ──
const HAIR_FRONT = {
  short: "M41 98 q-6 -62 59 -62 q65 0 59 62 q-8 -22 -26 -30 q-14 12 -33 12 q-19 0 -33 -12 q-18 8 -26 30z",
  spiky: "M40 100 l-8 -34 l18 8 l2 -30 l20 14 l14 -26 l14 20 l14 -20 l14 26 l20 -14 l2 30 l18 -8 l-8 34 q-8 -22 -26 -30 q-14 12 -34 12 q-20 0 -34 -12 q-18 8 -26 30z",
  swept: "M41 100 q-6 -64 59 -64 q66 0 59 64 q-10 -26 -24 -34 q-30 26 -78 24 q-10 4 -16 10z",
  long: "M40 104 q-6 -68 60 -68 q66 0 60 68 q2 30 -8 50 q-6 -40 -18 -70 q-14 -12 -34 -16 q-20 4 -34 16 q-12 30 -18 70 q-10 -20 -8 -50z",
  parted: "M41 100 q-6 -64 59 -64 q65 0 59 64 q-6 -24 -22 -34 q-18 -8 -37 -10 q-19 2 -37 10 q-16 10 -22 34z",
  curly: "M40 100 a14 14 0 0 1 4 -26 a16 16 0 0 1 14 -26 a18 18 0 0 1 26 -14 a18 18 0 0 1 32 0 a18 18 0 0 1 26 14 a16 16 0 0 1 14 26 a14 14 0 0 1 4 26 q-10 -20 -26 -26 q-12 8 -34 8 q-22 0 -34 -8 q-16 6 -26 26z",
  // Big and messy (the Magnus bot, Jack 2026-10-18).
  messy: "M34 104 q-10 -14 -4 -26 q-12 -6 -6 -20 q10 2 14 -4 q-6 -18 8 -26 q8 8 14 4 q2 -18 20 -20 q4 10 12 8 q10 -16 28 -10 q0 10 8 12 q16 -8 26 6 q-6 8 0 14 q16 2 16 20 q-8 4 -6 10 q12 8 6 22 q-6 -2 -10 4 q-4 -20 -20 -32 q-8 12 -22 8 q-4 12 -20 10 q-6 10 -22 6 q-10 12 -26 6 q-10 10 -16 28z",
  mohawk: "M86 64 q-6 -28 2 -52 l8 10 l4 -18 l8 14 l6 -14 l6 18 q8 24 -6 44z",
};
const FRONT_OF = { ponytail: "swept", braids: "parted", bun: "parted" };

function hairFront(L) {
  const d = HAIR_FRONT[FRONT_OF[L.hair] || L.hair];
  if (!d) return "";
  return `<path d="${d}" ${st(L.hairColour)}/>` + shade("M100 36 q66 0 59 64 q-8 -22 -24 -32 q8 -20 -35 -32z", 0.1);
}

function hairBack(L) {
  const h = L.hairColour;
  switch (L.hair) {
    case "long":
      return `<path d="M38 96 q-8 72 8 100 q54 14 108 0 q16 -28 8 -100z" ${st(h)}/>`;
    case "ponytail":
      return `<path d="M146 66 q46 4 44 62 q-2 36 -22 48 q8 -38 -8 -68 q-4 -24 -14 -42z" ${st(h)}/>` +
        `<rect x="150" y="62" width="16" height="12" rx="4" ${st(darken(h, 0.4), 3)}/>`;
    case "bun":
      return `<circle cx="100" cy="34" r="22" ${st(h)}/>`;
    case "curly":
      return `<ellipse cx="100" cy="102" rx="74" ry="66" ${st(h)}/>`;
    case "messy":
      return `<path d="M30 112 q-14 -30 2 -56 q8 -30 40 -38 q28 -14 56 -4 q34 8 42 40 q16 26 2 58z" ${st(h)}/>`;
    default:
      return "";
  }
}

// Braids hang in front of the shoulders, so they go over the body.
function hairMid(L) {
  if (L.hair !== "braids") return "";
  let s = "";
  for (const x of [44, 156]) {
    for (const y of [124, 144, 164]) s += `<ellipse cx="${x}" cy="${y}" rx="11" ry="12" ${st(L.hairColour)}/>`;
    s += `<rect x="${x - 7}" y="176" width="14" height="8" rx="3" ${st(L.trim, 3)}/>`;
    s += `<path d="M${x - 6} 184 l6 14 l6 -14z" ${st(L.hairColour, 3)}/>`;
  }
  return s;
}

// ── Headwear ──
function dome(colour) {
  return `<path d="M44 92 q0 -56 56 -58 q56 2 56 58 z" ${st(colour)}/>` + shade("M100 34 q56 2 56 58 h-40 q0 -40 -16 -58z", 0.12);
}

function headwear(L) {
  const t = L.trim;
  switch (L.headwear) {
    case "viking":
      return dome(STEEL) +
        `<path d="M48 70 q-30 -6 -34 -44 q14 18 38 22 z" ${st("#f4ecd8")}/><path d="M152 70 q30 -6 34 -44 q-14 18 -38 22 z" ${st("#f4ecd8")}/>` +
        `<rect x="40" y="86" width="120" height="14" rx="6" ${st(GOLD)}/><rect x="94" y="40" width="12" height="50" rx="4" ${st(GOLD)}/>`;
    case "winged":
      return dome("#d7dde4") +
        `<path d="M48 78 q-34 -8 -40 -44 q14 6 22 16 q-2 -18 8 -30 q12 18 16 46z" ${st("#fdfaf2")}/>` +
        `<path d="M152 78 q34 -8 40 -44 q-14 6 -22 16 q2 -18 -8 -30 q-12 18 -16 46z" ${st("#fdfaf2")}/>` +
        line("M24 46 q8 10 18 14 M176 46 q-8 10 -18 14", "#c8ccd2", 3) +
        `<rect x="40" y="86" width="120" height="14" rx="6" ${st(GOLD)}/><circle cx="100" cy="93" r="7" ${st(t, 3)}/>`;
    case "helm":
      return `<path d="M38 104 q-6 -70 62 -70 q68 0 62 70 l-12 18 q-2 -46 -50 -48 q-48 2 -50 48z" ${st("#b8c0c8")}/>` +
        shade("M100 34 q68 0 62 70 l-12 18 q-2 -46 -30 -48 q0 -24 -20 -40z", 0.12) +
        `<rect x="95" y="68" width="10" height="34" rx="4" ${st("#b8c0c8", 3.5)}/>` +
        `<path d="M92 36 q8 -30 30 -32 q-6 14 -10 32" ${st(t)}/>`;
    case "wizard":
      return `<path d="M34 80 q66 -26 132 0 q-10 14 -66 14 q-56 0 -66 -14z" ${st(L.outfitColour)}/>` +
        `<path d="M58 74 q18 -44 26 -74 q24 6 52 -6 q-18 16 -18 30 q8 34 14 52 q-36 10 -74 -2z" ${st(L.outfitColour)}/>` +
        shade("M118 -6 q-18 16 -18 30 q8 34 14 52 h22 q-6 -18 -14 -52 q0 -14 -4 -30z", 0.14) +
        line("M62 70 q38 10 70 0", O, 11) + line("M62 70 q38 10 70 0", t, 6) +
        `<path d="M96 30 l4 8 l9 1 l-7 6 l2 9 l-8 -5 l-8 5 l2 -9 l-7 -6 l9 -1z" fill="${GOLD}" stroke="${O}" stroke-width="2"/>`;
    case "ranger":
      return `<path d="M30 70 q70 -30 140 0 q-14 10 -70 10 q-56 0 -70 -10z" ${st(L.outfitColour)}/>` +
        `<path d="M52 66 q26 -66 96 -56 q-30 18 -10 58z" ${st(L.outfitColour)}/>` +
        shade("M148 10 q-30 18 -10 58 h22 q-14 -36 -12 -58z", 0.14) +
        `<path d="M140 30 l26 -16 l-6 22z" ${st(t)}/>`;
    case "crown":
      return `<path d="M58 62 l4 -36 l20 18 l18 -28 l18 28 l20 -18 l4 36 z" ${st(GOLD)}/>` +
        shade("M100 16 l18 28 l20 -18 l4 36 h-42z", 0.1) +
        `<circle cx="100" cy="50" r="6" ${st(t, 3)}/><circle cx="76" cy="54" r="4" ${st("#2e86de", 2.5)}/><circle cx="124" cy="54" r="4" ${st("#2e86de", 2.5)}/>`;
    case "tiara":
      return `<path d="M62 66 q38 -20 76 0 l-2 8 q-36 -16 -72 0z" ${st(GOLD, 3.5)}/>` +
        `<path d="M100 38 l9 12 l-9 12 l-9 -12z" ${st(t, 3.5)}/>` +
        `<path d="M76 58 l4 -10 l4 8z M124 58 l-4 -10 l-4 8z" ${st(GOLD, 3)}/>`;
    case "bandana":
      return `<path d="M42 88 q-2 -52 58 -54 q60 2 58 54 q-58 -24 -116 0z" ${st(t)}/>` +
        shade("M100 34 q60 2 58 54 q-20 -8 -36 -10 q0 -30 -22 -44z", 0.12) +
        `<path d="M154 72 l26 -10 l-6 18z" ${st(t, 3.5)}/><path d="M154 78 l20 14 l-16 4z" ${st(t, 3.5)}/>`;
    case "santa":
      // Floppy red hat: the tip droops over to one side with a bobble.
      return `<path d="M42 86 q2 -50 48 -62 q48 -12 80 18 q22 22 16 52 h-14 q0 -22 -14 -36 q-4 16 2 30z" ${st("#d6332f")}/>` +
        shade("M90 24 q48 -12 80 18 q22 22 16 52 h-14 q0 -22 -14 -36 q-30 -30 -68 -34z", 0.14) +
        `<rect x="38" y="76" width="124" height="22" rx="11" ${st("#fdfaf2")}/>` +
        line("M54 84 h10 M80 82 h10 M108 82 h10 M134 84 h10", "#e3dccb", 3) +
        `<circle cx="180" cy="98" r="14" ${st("#fdfaf2")}/><circle cx="176" cy="94" r="4" fill="#fff"/>`;
    case "pirate":
      return `<path d="M24 80 q22 -10 32 -38 q44 -26 88 0 q10 28 32 38 q-76 -20 -152 0z" ${st("#2c2c2c")}/>` +
        line("M30 77 q70 -16 140 0", GOLD, 3) +
        `<circle cx="100" cy="50" r="8" ${st("#f4ecd8", 3)}/>` + line("M92 62 l16 6 M108 62 l-16 6", "#f4ecd8", 3);
    default:
      return "";
  }
}

// Full knight's helm, in place of the whole head.
function knightHelm(L) {
  return `<ellipse cx="100" cy="94" rx="60" ry="56" ${st("#b8c0c8")}/>` +
    shade("M100 38 a60 56 0 0 1 0 112 q34 -12 34 -56 q0 -44 -34 -56z") +
    `<path d="M58 96 h84 v26 q0 8 -8 8 h-68 q-8 0 -8 -8 z" ${st(O)}/>` +
    `<path d="M64 108 h72" stroke="#59616b" stroke-width="3"/>` +
    `<ellipse cx="82" cy="112" rx="5" ry="6" fill="#ffd34d"/><ellipse cx="118" cy="112" rx="5" ry="6" fill="#ffd34d"/>` +
    `<path d="M100 38 v56" stroke="${O}" stroke-width="3.5"/>` +
    `<path d="M100 36 q10 -26 34 -26 q-12 10 -14 28" ${st(L.trim)}/>`;
}

// Hood up: the hood frames a smaller face.
function hoodHead(L, mood) {
  const c = L.outfitColour;
  return `<path d="M36 104 q-4 -78 64 -80 q68 2 64 80 q0 50 -64 54 q-64 -4 -64 -54z" ${st(c)}/>` +
    shade("M100 24 q68 2 64 80 q0 50 -64 54 q40 -20 40 -70 q0 -50 -40 -64z", 0.18) +
    `<ellipse cx="100" cy="106" rx="46" ry="38" ${st(L.skin)}/>` +
    `<g transform="translate(100 104) scale(.78) translate(-100 -110)">${face(L, 110, mood)}${faceExtra(L, 110)}</g>`;
}

// ── Held things ──
function weaponBack(L) {
  switch (L.weapon) {
    case "sword":
      return `<g transform="translate(150 174) rotate(-15)">` +
        `<path d="M-7 -8 v-78 l7 -12 l7 12 v78 z" ${st("#e6ebf0")}/><path d="M0 -94 v84" stroke="#b8c2cc" stroke-width="3"/>` +
        `<rect x="-20" y="-10" width="40" height="9" rx="4" ${st(GOLD)}/>` +
        `<rect x="-4.5" y="-2" width="9" height="20" rx="3" ${st("#5b3b2a")}/><circle cx="0" cy="22" r="6" ${st(GOLD)}/></g>`;
    case "axe":
      return `<g transform="translate(156 172) rotate(-20)">` +
        `<rect x="-5" y="-70" width="10" height="110" rx="4" ${st(WOOD)}/>` +
        `<path d="M5 -66 q38 -6 40 28 q-20 2 -40 -4 z" ${st("#d7dde4")}/><path d="M-5 -66 q-38 -6 -40 28 q20 2 40 -4 z" ${st("#d7dde4")}/>` +
        `<path d="M5 -48 q22 -2 32 10" fill="none" stroke="#fff" stroke-width="3" opacity=".7"/></g>`;
    case "hammer":
      return `<g transform="translate(152 178) rotate(-12)">` +
        `<rect x="-5" y="-92" width="10" height="122" rx="4" ${st(WOOD)}/>` +
        `<rect x="-28" y="-118" width="56" height="36" rx="7" ${st(STEEL)}/>` + shade("M4 -116 h18 q6 0 6 6 v22 q0 6 -6 6 h-18z", 0.15) +
        `<rect x="-30" y="-104" width="60" height="8" rx="3" ${st(GOLD, 3)}/></g>`;
    case "spear":
      return `<g transform="translate(156 186)">` +
        `<rect x="-4" y="-150" width="8" height="190" rx="3" ${st(WOOD)}/>` +
        `<path d="M0 -182 l12 26 l-12 10 l-12 -10 z" ${st("#e6ebf0")}/>` +
        `<rect x="-8" y="-150" width="16" height="8" rx="3" ${st(L.trim)}/></g>`;
    case "staff": {
      const orb = L.orb || "#9b59ff";
      return `<g transform="translate(150 182)">` +
        line("M-4 40 v-170 q0 -24 22 -26 q20 2 18 22", O, 14) + line("M-4 40 v-170 q0 -24 22 -26 q20 2 18 22", WOOD, 7) +
        `<circle cx="0" cy="-128" r="13" fill="${orb}" stroke="${O}" stroke-width="${SW}"/><circle cx="-4" cy="-132" r="4" fill="#fff" opacity=".8"/></g>`;
    }
    default:
      return "";
  }
}

function dagger(x, y, rot) {
  return `<g transform="translate(${x} ${y}) rotate(${rot})">` +
    `<path d="M-6 -6 v-38 l6 -10 l6 10 v38 z" ${st("#e6ebf0")}/>` +
    `<rect x="-14" y="-8" width="28" height="7" rx="3" ${st("#c0392b")}/><rect x="-4" y="-2" width="8" height="14" rx="3" ${st("#2c2c2c")}/></g>`;
}

function shield(L) {
  if (L.shield === "round") {
    return `<g transform="translate(48 178)"><circle r="30" ${st(L.shieldColour)}/>` +
      `<circle r="25" fill="none" stroke="${darken(L.shieldColour, 0.3)}" stroke-width="5"/>` +
      `<path d="M-30 0 h60 M0 -30 v60" stroke="${O}" stroke-width="3" opacity=".45"/>` +
      `<circle r="30" fill="none" stroke="${O}" stroke-width="${SW}"/><circle r="9" ${st("#d7dde4")}/></g>`;
  }
  if (L.shield === "kite") {
    return `<g transform="translate(52 180)"><path d="M-26 -30 h52 v20 q0 32 -26 46 q-26 -14 -26 -46 z" ${st(L.shieldColour)}/>` +
      shade("M0 -30 h26 v20 q0 32 -26 46z", 0.12) +
      `<path d="M0 -24 v50 M-16 -6 h32" stroke="${L.trim}" stroke-width="7" stroke-linecap="round"/></g>`;
  }
  return "";
}

function bow() {
  return `<g transform="translate(46 176)">` +
    line("M-6 -60 q46 60 0 120", O, 13) + line("M-6 -60 q46 60 0 120", WOOD, 6) +
    `<path d="M-6 -60 v120" stroke="#f4ecd8" stroke-width="2"/>` +
    line("M-40 0 h50", O, 7) + line("M-40 0 h50", "#c2a878", 3) +
    `<path d="M12 0 l-10 -6 v12 z" ${st("#e6ebf0", 3)}/></g>`;
}

function quiver() {
  let s = `<path d="M150 150 l18 -50 l10 4 l-16 50z" ${st(WOOD)}/>`;
  for (let i = 0; i < 3; i++) s += `<path d="M${165 + i * 5} ${100 - i * 2} l-4 -14 l8 2z" ${st("#f4ecd8", 3)}/>`;
  return s;
}

export function flagSvg(colour) {
  return `<g class="jav-flag"><rect x="44" y="36" width="8" height="194" rx="3" ${st(WOOD)}/>` +
    `<g class="jav-cloth"><path d="M46 42 q-26 -10 -58 2 v46 q32 -10 58 0 z" ${st(colour)}/>` +
    `<path d="M46 42 q-26 -10 -58 2 v12 q32 -10 58 -2z" fill="#fff" opacity=".22"/></g>` +
    `<circle cx="48" cy="34" r="7" ${st(GOLD)}/></g>`;
}

// The flag of a crystal ball holder floats beside him on a faint violet glow.
// It flies on his right (mirrored), where the drawing has room, so the board
// can size him like any other raider (Jack, 2026-10-23).
function flagFloat(colour) {
  return `<g class="jav-flag-float"><ellipse cx="171" cy="186" rx="11" ry="3.5" fill="#9b6dff" opacity=".45"/>` +
    `<g transform="translate(207 16) scale(-.75 .75)">${flagSvg(colour)}</g></g>`;
}

// Majestic outfit: a long white cloak behind the body.
function majesticCape() {
  return `<path d="M70 144 q-30 34 -34 70 q64 8 128 0 q-4 -36 -34 -70 z" ${st(MAJ_WHITE)}/>` +
    `<path d="M100 146 q34 2 34 0 q26 34 30 68 q-30 4 -64 4z" fill="${MAJ_SHADE}" opacity=".6"/>`;
}

// Crystal ball held in front in both hands (the admin's, Jack 2026-10-23).
function crystalArms(L) {
  const sl = sleeveColour(L);
  const armPath = (sx, hx) => `M${sx} 152 Q${sx} 184 ${hx} 200`;
  return line(armPath(62, 84), O, 21) + line(armPath(62, 84), sl, 12) +
    line(armPath(138, 116), O, 21) + line(armPath(138, 116), sl, 12) +
    `<path d="M66 180 q-6 18 8 28 l14 -12 q-12 -4 -22 -16z" ${st(sl)}/>` +
    `<path d="M134 180 q6 18 -8 28 l-14 -12 q12 -4 22 -16z" ${st(sl)}/>`;
}
function crystalBall(L) {
  // A dark violet ball: mist swirls inside, tiny stars twinkle, a core
  // glows and two motes orbit while the aura shifts violet to teal.
  const star = (x, y, r, d) =>
    `<path class="jav-twinkle" style="animation-delay:${d}s" d="M${x} ${y - r} L${x + r * 0.3} ${y - r * 0.3} L${x + r} ${y} L${x + r * 0.3} ${y + r * 0.3} L${x} ${y + r} L${x - r * 0.3} ${y + r * 0.3} L${x - r} ${y} L${x - r * 0.3} ${y - r * 0.3}z" fill="#fff"/>`;
  return `<radialGradient id="jav-crystal" cx=".38" cy=".34" r=".75"><stop offset="0" stop-color="#cdbcff"/>` +
    `<stop offset=".45" stop-color="#5b3fb8"/><stop offset="1" stop-color="#1d1446"/></radialGradient>` +
    `<radialGradient id="jav-crystal-aura"><stop class="jav-aura-stop" offset=".5" stop-color="#9b6dff" stop-opacity=".65"/><stop class="jav-aura-stop" offset="1" stop-color="#9b6dff" stop-opacity="0"/></radialGradient>` +
    `<clipPath id="jav-crystal-clip"><circle cx="100" cy="187" r="18"/></clipPath>` +
    `<circle class="jav-crystal-aura" cx="100" cy="186" r="40" fill="url(#jav-crystal-aura)"/>` +
    `<path d="M86 206 h28 l-4 8 h-20z" ${st(MAJ_SILVER, 3.5)}/>` +
    `<circle cx="100" cy="187" r="20" fill="url(#jav-crystal)" stroke="${O}" stroke-width="${SW}"/>` +
    `<g clip-path="url(#jav-crystal-clip)">` +
    `<g class="jav-mist-a"><ellipse cx="94" cy="190" rx="12" ry="6" fill="#c77dff" opacity=".45"/><path d="M88 194 q6 -12 16 -8 q8 4 2 12" fill="none" stroke="#e6d4ff" stroke-width="2.5" stroke-linecap="round" opacity=".8"/></g>` +
    `<g class="jav-mist-b"><ellipse cx="106" cy="184" rx="11" ry="5" fill="#4fd8ff" opacity=".4"/><path d="M110 180 q-6 10 -14 8" fill="none" stroke="#bff3ff" stroke-width="2" stroke-linecap="round" opacity=".7"/></g>` +
    `<circle class="jav-crystal-core" cx="100" cy="188" r="6" fill="#f1e6ff"/>` +
    star(92, 196, 2.6, 0) + star(108, 192, 2.2, -0.7) + star(103, 179, 1.8, -1.4) + star(90, 184, 1.6, -2.1) +
    `</g>` +
    `<ellipse cx="93" cy="179" rx="5" ry="3" fill="#fff" opacity=".75"/>` +
    `<g class="jav-orbit"><circle cx="128" cy="187" r="2.2" fill="#e6d4ff"/><circle cx="72" cy="187" r="1.6" fill="#9ff0ff"/></g>` +
    `<circle cx="84" cy="202" r="8" ${st(L.skin)}/><circle cx="116" cy="202" r="8" ${st(L.skin)}/>`;
}

const MOOD = { cheer: "happy", wave: "happy", taunt: "fierce", ready: "fierce", grab: "fierce", sad: "sad" };

// The character as an SVG string. Poses animate through AVATAR_CSS, which
// must be on the page (ensureAvatarCss).
export function renderAvatar(L, { pose = "idle", flag = null, className = "" } = {}) {
  // The majestic outfit makes him Gandalf the White: long white hair and beard.
  if (L.outfit === "majestic") L = { ...L, hair: "long", hairColour: MAJ_HAIR, face: "wizardbeard" };
  const mood = MOOD[pose] || "neutral";
  const hood = L.headwear === "hood";
  const knight = L.headwear === "knight";
  const twoBlades = L.weapon === "daggers";

  let head;
  if (knight) head = knightHelm(L);
  else if (hood) head = hoodHead(L, mood);
  else head = headBase(L.skin) + face(L, 110, mood) + faceExtra(L, 110) + hairFront(L) + headwear(L);

  // A crystal ball is never put down: the flag floats beside him instead.
  const crystal = L.weapon === "crystal";
  const floatFlag = crystal && flag ? flagFloat(flag) : "";
  const back = (L.outfit === "majestic" ? majesticCape() : "") + (hood ? "" : hairBack(L)) + (hood || knight ? "" : ears(L)) + (L.weapon === "bow" && !flag ? quiver() : "");
  let held = "";
  if (flag && !crystal) held = flagSvg(flag);
  else if (L.weapon === "bow") held = bow();
  else if (twoBlades) held = dagger(50, 186, -30);
  else if (L.weapon !== "gloves" && !crystal) held = shield(L);

  const upper =
    back +
    grp("jav-arm-r", grp("jav-wpn", weaponBack(L))) +
    torso(L) +
    (crystal ? crystalArms(L) : grp("jav-arm-l", arm(L, -1)) + grp("jav-arm-r", arm(L, 1))) +
    (hood || knight ? "" : hairMid(L)) +
    grp("jav-head", head) +
    (crystal ? crystalBall(L) : "") +
    grp("jav-arm-r", grp("jav-wpn", twoBlades ? dagger(150, 186, 30) : "")) +
    grp("jav-arm-l", grp("jav-held", held));

  const v = VIEWBOX;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${v.x} ${v.y} ${v.w} ${v.h}" class="jav jav-${pose}${L.outfit === "majestic" ? " jav-float" : ""} ${className}">` +
    `<ellipse class="jav-shadow" cx="100" cy="228" rx="52" ry="8" fill="#000" opacity=".25"/>` +
    floatFlag +
    (L.outfit === "majestic"
      ? `<g class="jav-hover"><g class="jav-all"><g class="jav-upper">${upper}</g></g></g></svg>`
      : `<g class="jav-all">${legs(L)}<g class="jav-upper">${upper}</g></g></svg>`);
}

// Pose animations. Arms swing about the shoulders; whatever a hand holds
// counter-rotates about the hand so weapons and flags stay upright.
export const AVATAR_CSS = `
svg.jav{overflow:visible;display:block;width:100%;height:100%}
svg.jav g{transform-box:view-box}
svg.jav .jav-arm-l{transform-origin:62px 152px}
svg.jav .jav-arm-r{transform-origin:138px 152px}
svg.jav .jav-wpn{transform-origin:142px 184px}
svg.jav .jav-held{transform-origin:58px 184px}
svg.jav .jav-leg-l{transform-origin:88px 220px}
svg.jav .jav-leg-r{transform-origin:112px 220px}
svg.jav .jav-head{transform-origin:100px 150px}
svg.jav .jav-upper{transform-origin:100px 200px}
svg.jav .jav-cloth{transform-origin:46px 66px;animation:jav-wave 1.2s ease-in-out infinite}
@keyframes jav-wave{50%{transform:skewY(-7deg) scaleX(.93)}}
svg.jav-idle .jav-upper,svg.jav-guard .jav-upper,svg.jav-ready .jav-upper{animation:jav-breathe 1.8s ease-in-out infinite}
@keyframes jav-breathe{50%{transform:translateY(3px) scaleY(.985)}}
svg.jav-idle .jav-head{animation:jav-nod 3.6s ease-in-out infinite}
@keyframes jav-nod{50%{transform:rotate(-3deg)}}
svg.jav-walk .jav-all{animation:jav-bob .3s ease-in-out infinite alternate}
@keyframes jav-bob{to{transform:translateY(-7px)}}
svg.jav-walk .jav-leg-l{animation:jav-lift .3s ease-in-out infinite alternate}
svg.jav-walk .jav-leg-r{animation:jav-lift .3s ease-in-out infinite alternate-reverse}
@keyframes jav-lift{from{transform:translateY(0)}to{transform:translateY(-8px)}}
svg.jav-walk .jav-arm-l{animation:jav-swing .3s ease-in-out infinite alternate-reverse}
svg.jav-walk .jav-arm-r{animation:jav-swing .3s ease-in-out infinite alternate}
@keyframes jav-swing{from{transform:rotate(-14deg)}to{transform:rotate(14deg)}}
svg.jav-ready .jav-arm-r{transform:rotate(-35deg)}
svg.jav-ready .jav-wpn{transform:rotate(35deg)}
svg.jav-taunt .jav-arm-r,svg.jav-cheer .jav-arm-r{animation:jav-raise-r .7s ease-in-out infinite}
svg.jav-taunt .jav-wpn,svg.jav-cheer .jav-wpn{animation:jav-raise-rw .7s ease-in-out infinite}
@keyframes jav-raise-r{0%,100%{transform:rotate(-100deg)}45%{transform:rotate(-125deg)}}
@keyframes jav-raise-rw{0%,100%{transform:rotate(100deg)}45%{transform:rotate(125deg)}}
svg.jav-wave .jav-arm-r{animation:jav-wave-r .4s ease-in-out infinite alternate}
svg.jav-wave .jav-wpn{animation:jav-wave-rw .4s ease-in-out infinite alternate}
@keyframes jav-wave-r{from{transform:rotate(-110deg)}to{transform:rotate(-150deg)}}
@keyframes jav-wave-rw{from{transform:rotate(110deg)}to{transform:rotate(150deg)}}
svg.jav-taunt .jav-upper{animation:jav-lean .7s ease-in-out infinite}
@keyframes jav-lean{45%{transform:rotate(-3deg) translateY(-2px)}}
svg.jav-cheer .jav-all{animation:jav-jump .7s cubic-bezier(.3,0,.4,1) infinite}
@keyframes jav-jump{0%,100%{transform:translateY(0)}45%{transform:translateY(-24px)}}
svg.jav-cheer .jav-arm-l{animation:jav-raise-l .7s ease-in-out infinite}
svg.jav-cheer .jav-held{animation:jav-raise-lw .7s ease-in-out infinite}
@keyframes jav-raise-l{0%,100%{transform:rotate(100deg)}45%{transform:rotate(125deg)}}
@keyframes jav-raise-lw{0%,100%{transform:rotate(-100deg)}45%{transform:rotate(-125deg)}}
svg.jav-cheer .jav-shadow{animation:jav-shrink .7s cubic-bezier(.3,0,.4,1) infinite;transform-box:fill-box;transform-origin:center}
@keyframes jav-shrink{45%{transform:scale(.7)}}
svg.jav-grab .jav-arm-l{animation:jav-reach .45s ease-out forwards}
svg.jav-grab .jav-held{animation:jav-reach-w .45s ease-out forwards}
@keyframes jav-reach{to{transform:rotate(60deg)}}
@keyframes jav-reach-w{to{transform:rotate(-60deg)}}
svg.jav-sad{filter:saturate(.55) brightness(.92)}
svg.jav-sad .jav-upper{transform:translateY(4px)}
svg.jav-sad .jav-head{transform:rotate(7deg) translateY(3px)}
.jav-glow .jav-glove-fill{animation:jav-glove-hue 1.8s linear infinite}
@keyframes jav-glove-hue{0%,100%{fill:#ff2e63}17%{fill:#ff9f1c}33%{fill:#ffe14d}50%{fill:#22e08a}67%{fill:#22b8ff}83%{fill:#b44dff}}
.jav-sparkle,.jav-glove-halo{display:none}
.jav-glow .jav-glove-halo{display:inline}
.jav-glow .jav-sparkle{display:inline;transform-box:fill-box;transform-origin:center;transform:scale(0);animation:jav-sparkle 1.4s ease-out infinite}
@keyframes jav-sparkle{0%{transform:scale(0) rotate(0)}15%{transform:scale(1.2) rotate(40deg)}35%{transform:scale(0) rotate(90deg)}100%{transform:scale(0) rotate(90deg)}}
svg.jav .jav-crystal-aura{transform-box:fill-box;transform-origin:center;animation:jav-crystal 2.6s ease-in-out infinite}
@keyframes jav-crystal{50%{opacity:.55;transform:scale(.86)}}
svg.jav .jav-aura-stop{animation:jav-aura-hue 5s ease-in-out infinite}
@keyframes jav-aura-hue{0%,100%{stop-color:#9b6dff}50%{stop-color:#3fd0e0}}
svg.jav .jav-mist-a{transform-origin:100px 187px;animation:jav-swirl 4s linear infinite}
svg.jav .jav-mist-b{transform-origin:100px 187px;animation:jav-swirl 6s linear infinite reverse}
svg.jav .jav-orbit{transform-origin:100px 187px;animation:jav-swirl 5s linear infinite}
@keyframes jav-swirl{to{transform:rotate(360deg)}}
svg.jav .jav-hover{animation:jav-hover 3s ease-in-out infinite}
svg.jav .jav-flag-float{animation:jav-hover 3s ease-in-out -1.5s infinite}
@keyframes jav-hover{0%,100%{transform:translateY(-10px)}50%{transform:translateY(-3px)}}
svg.jav-float .jav-shadow{transform-box:fill-box;transform-origin:center;animation:jav-hover-shadow 3s ease-in-out infinite}
@keyframes jav-hover-shadow{0%,100%{transform:scale(.7);opacity:.15}50%{transform:scale(.85);opacity:.22}}
svg.jav .jav-crystal-core{transform-box:fill-box;transform-origin:center;animation:jav-core 2.6s ease-in-out infinite}
@keyframes jav-core{0%,100%{opacity:.25;transform:scale(.7)}50%{opacity:.75;transform:scale(1.15)}}
svg.jav .jav-twinkle{transform-box:fill-box;transform-origin:center;animation:jav-twinkle 2.8s ease-in-out infinite}
@keyframes jav-twinkle{0%,60%,100%{opacity:0;transform:scale(.3)}75%{opacity:1;transform:scale(1)}}
@media (prefers-reduced-motion: reduce){svg.jav *{animation:none!important}}
`;

// Adds AVATAR_CSS to a document once (the page, or the game iframe).
export function ensureAvatarCss(doc = document) {
  if (!doc || doc.getElementById("jangle-avatar-css")) return;
  const el = doc.createElement("style");
  el.id = "jangle-avatar-css";
  el.textContent = AVATAR_CSS;
  doc.head.appendChild(el);
}
